import { Link, NavLink, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { makeStyles, useMediaQuery } from '@material-ui/core';
import BusinessIcon from '@material-ui/icons/BusinessRounded';

import { useSetAuth } from 'hooks';
import { useGetCurrentUserQuery, usePrefetch } from 'utils/wemble-api';

import dashboardIcon from '../../assets/icons/home.svg';
import statisticsIcon from '../../assets/icons/statistics.svg';
import projectsIcon from '../../assets/icons/projects.svg';
import competenceIcon from '../../assets/icons/competences.svg';
import settingsIcon from '../../assets/icons/settings.svg';
import usersIcon from '../../assets/icons/users.svg';
import logoutIcon from '../../assets/icons/logout.svg';
import wemble from '../../assets/wemble.svg';
import organization from 'utils/organization';

/** Consumed by layouts/top bars that sit next to the fixed sidebar. */
export const SIDEBAR_WIDTH = 92;

const SIDEBAR_GUTTER = 12;

/**
 * Nav items step through three tones: idle gray -> hover (halfway to black) -> selected black.
 * The icon SVGs ship in mixed near-blacks (#000 and #333), so each filter starts with
 * brightness(0) to flatten them before tinting — otherwise the two sets land on different grays.
 * The invert percentages are the same tones as the label colors beside them.
 */
const IDLE_TONE = '#757575';
const HOVER_TONE = '#3B3B3B';
const IDLE_FILTER = 'brightness(0) invert(46%)';
const HOVER_FILTER = 'brightness(0) invert(23%)';

const useStyles = makeStyles((theme) => ({
  sidebar: {
    backgroundColor: 'white',
    height: '100%',
    width: SIDEBAR_WIDTH,
    position: 'fixed',
    zIndex: 1,
    top: 0,
    left: 0,
    padding: `20px ${SIDEBAR_GUTTER}px 0`,
    boxSizing: 'border-box',
  },
  // The anchor has to be block-level for the mark's auto margins to centre it.
  sidebar__logoLink: {
    display: 'block',
  },
  sidebar__logo: {
    display: 'block',
    width: 48,
    margin: '0 auto',
  },
  sidebar__links: {
    marginTop: 30,
  },
  links__link: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textDecoration: 'none !important',
    width: '100%',
    height: 55,
    padding: '0 4px',
    boxSizing: 'border-box',
    marginBottom: 10,
    borderRadius: 8,
    backgroundColor: 'transparent',
    transition: 'background-color 0.15s ease-in-out, transform 0.15s ease-in-out',
    '& > img': {
      filter: IDLE_FILTER,
      width: 24,
      transition: 'filter 0.15s ease-in-out',
    },
    '& > svg': {
      color: 'black',
      filter: IDLE_FILTER,
      width: 24,
      height: 24,
      transition: 'filter 0.15s ease-in-out',
    },
    // Hover only where a pointer hovers; on iPad it would stick after a tap.
    '@media (hover: hover)': {
      '&:hover': {
        backgroundColor: theme.palette.gray.g7,
        '& > img': {
          filter: HOVER_FILTER,
        },
        '& > svg': {
          filter: HOVER_FILTER,
        },
        '& > span': {
          color: HOVER_TONE,
        },
      },
    },
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none' as const,
    userSelect: 'none' as const,
    '&:active': {
      transform: 'scale(0.97)',
    },
  },
  // Every label gets the same 12px line box, so each item's icon + label
  // block is the same height and the icons line up down the rail whatever
  // the label's font size.
  links__linkLabel: {
    fontSize: 10,
    lineHeight: '12px',
    height: 12,
    fontWeight: 500,
    color: IDLE_TONE,
    marginTop: 4,
    whiteSpace: 'nowrap',
    transition: 'color 0.15s ease-in-out',
  },
  // "Competencies" is the longest label; slightly smaller and tighter so it
  // fits the item's width.
  links__linkLabel_long: {
    fontSize: 9,
    letterSpacing: -0.15,
  },
  // Selected sits on g9, two steps darker than the g7 hover preview, so the two read apart.
  links__link_active: {
    backgroundColor: theme.palette.gray.g9,
    '& > img': {
      filter: 'brightness(0)',
    },
    '& > svg': {
      filter: 'brightness(0)',
    },
    '& > span': {
      color: 'black',
    },
    // The selected item stays fully black on hover rather than dropping to the hover tone.
    '&:hover': {
      backgroundColor: theme.palette.gray.g9,
      '& > img': {
        filter: 'brightness(0)',
      },
      '& > svg': {
        filter: 'brightness(0)',
      },
      '& > span': {
        color: 'black',
      },
    },
  },
  links__logout: {
    position: 'absolute',
    bottom: 16,
    left: SIDEBAR_GUTTER,
    right: SIDEBAR_GUTTER,
    width: 'auto',
  },
}));

const SideBar = () => {
  const classes = useStyles();
  const setAuth = useSetAuth();
  const { pathname } = useLocation();
  const smallDevice = useMediaQuery('(max-width:1117px)');
  const prefetchLevelOfSkills = usePrefetch('getLevelOfSkills');
  const prefetchCompetences = usePrefetch('getCompetences');

  const { data: user } = useGetCurrentUserQuery();

  if (!user) return null;

  const organizationConfig = organization(user.company);

  const activeClass = classNames(classes.links__link_active, 'active');

  return (
    <div className={classes.sidebar}>
      <Link to="/home" className={classes.sidebar__logoLink}>
        <img alt="logo" src={wemble} className={classes.sidebar__logo} />
      </Link>
      <div className={classes.sidebar__links}>
        {
          <NavLink className={classes.links__link} to="/home" activeClassName={activeClass}>
            <img src={dashboardIcon} alt="dashboard" />
            <span className={classes.links__linkLabel}>Home</span>
          </NavLink>
        }

        {!organizationConfig.disableCompetences && (
          <NavLink
            className={classes.links__link}
            to="/competences"
            activeClassName={activeClass}
            onMouseEnter={() => { prefetchLevelOfSkills(undefined, { ifOlderThan: 500 }); prefetchCompetences(undefined, { ifOlderThan: 10 }); }}
          >
            <img src={competenceIcon} alt="competences" />
            <span className={classNames(classes.links__linkLabel, classes.links__linkLabel_long)}>Competencies</span>
          </NavLink>
        )}

        {!organizationConfig.disableProjects && (
          <NavLink className={classes.links__link} to="/projects" activeClassName={activeClass}>
            <img src={projectsIcon} alt="projects" />
            <span className={classes.links__linkLabel}>Projects</span>
          </NavLink>
        )}

        {(user.admin || user.administratorPriviliges) && !user.companyAdmin && (
          <NavLink className={classes.links__link} to="/users" activeClassName={activeClass}>
            <img src={usersIcon} alt="users" />
            <span className={classes.links__linkLabel}>Users</span>
          </NavLink>
        )}
        {
          <NavLink className={classes.links__link} to="/settings" activeClassName={activeClass}>
            <img src={settingsIcon} alt="settings" />
            <span className={classes.links__linkLabel}>Settings</span>
          </NavLink>
        }
        {!smallDevice && (
          <NavLink className={classes.links__link} to="/statistics" activeClassName={activeClass}>
            <img src={statisticsIcon} alt="statistics" />
            <span className={classes.links__linkLabel}>Statistics</span>
          </NavLink>
        )}
        {user.companyAdmin && (
          <NavLink
            className={classes.links__link}
            to="/groups"
            isActive={() => ['/groups', '/offices', '/users'].some((route) => pathname.startsWith(route))}
            activeClassName={activeClass}
          >
            <BusinessIcon />
            <span className={classes.links__linkLabel}>Admin</span>
          </NavLink>
        )}
        <Link
          className={classNames(classes.links__link, classes.links__logout)}
          to="/"
          onClick={() => setAuth(false)}
        >
          <img
            src={logoutIcon}
            alt="logout"
            style={{
              transform: 'rotate(90deg)',
            }}
          />
          <span className={classes.links__linkLabel}>Log out</span>
        </Link>
      </div>
    </div>
  );
};

export default SideBar;
